import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface Stats {
  todayTrials: number;
  todayActiveLessons: number;
  weeklyLessons: number;
  monthlyRevenue: number;
  monthlyTeacherCommission: number;
  pendingReceipts: number;
  trialConversions: number;
  activeTeachers: number;
}

interface StatCardProps {
  icon: string; label: string; value: string | number;
  subLabel?: string; iconBg: string; iconColor: string;
  trend?: { value: string; up: boolean }; highlight?: boolean;
}

function StatCard({ icon, label, value, subLabel, iconBg, iconColor, trend, highlight }: StatCardProps) {
  return (
    <div className={`bg-white rounded-2xl p-5 border flex items-start gap-4 transition-all hover:-translate-y-0.5 ${highlight ? 'border-[#1a2f5a]/20' : 'border-gray-100'}`}>
      <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${iconBg} flex-shrink-0`}>
        <i className={`${icon} text-xl ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 font-medium mb-0.5">{label}</p>
        <p className={`text-2xl font-bold leading-tight ${highlight ? 'text-[#1a2f5a]' : 'text-gray-800'}`}>{value}</p>
        {subLabel && <p className="text-xs text-gray-400 mt-0.5">{subLabel}</p>}
        {trend && (
          <div className={`flex items-center gap-1 mt-1.5 text-xs font-semibold ${trend.up ? 'text-emerald-600' : 'text-red-500'}`}>
            <i className={trend.up ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} />
            {trend.value}
          </div>
        )}
      </div>
    </div>
  );
}

export default function StatsCards() {
  const [stats, setStats] = useState<Stats>({ todayTrials: 0, todayActiveLessons: 0, weeklyLessons: 0, monthlyRevenue: 0, monthlyTeacherCommission: 0, pendingReceipts: 0, trialConversions: 0, activeTeachers: 0 });

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
    const monthStart = today.slice(0, 7) + '-01';

    const fetchStats = async () => {
      const [
        { count: todayTrials },
        { count: todayActive },
        { count: weeklyLessons },
        { data: monthlyPay },
        { count: pendingReceipts },
        { count: conversions },
        { count: activeTeachers },
      ] = await Promise.all([
        supabase.from('egtpanel_deneme_dersleri').select('id', { count: 'exact', head: true }).eq('planlanan_tarih', today),
        supabase.from('egtpanel_aktif_dersler').select('id', { count: 'exact', head: true }).eq('ders_tarihi', today),
        supabase.from('egtpanel_aktif_dersler').select('id', { count: 'exact', head: true }).gte('ders_tarihi', weekAgo),
        supabase.from('egtpanel_odemeler').select('tutar, ogretmen_payi').eq('yonetici_onay', true).gte('dekont_tarihi', monthStart),
        supabase.from('egtpanel_odemeler').select('id', { count: 'exact', head: true }).eq('yonetici_onay', false).neq('odeme_durumu', 'İptal'),
        supabase.from('egtpanel_deneme_dersleri').select('id', { count: 'exact', head: true }).eq('sonuc', 'Aktif Derse Döndü').gte('olusturma_tarihi', monthStart),
        supabase.from('egtpanel_ogretmenler').select('id', { count: 'exact', head: true }).eq('aktif', true),
      ]);

      const monthlyRevenue = (monthlyPay || []).reduce((a, p) => a + Number(p.tutar), 0);
      const monthlyTeacherCommission = (monthlyPay || []).reduce((a, p) => a + Number(p.ogretmen_payi || 0), 0);

      setStats({
        todayTrials: todayTrials || 0,
        todayActiveLessons: todayActive || 0,
        weeklyLessons: weeklyLessons || 0,
        monthlyRevenue,
        monthlyTeacherCommission,
        pendingReceipts: pendingReceipts || 0,
        trialConversions: conversions || 0,
        activeTeachers: activeTeachers || 0,
      });
    };
    fetchStats();
  }, []);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard icon="ri-calendar-check-line" label="Bugünkü Deneme Dersleri" value={stats.todayTrials} subLabel="Planlandı" iconBg="bg-blue-50" iconColor="text-[#3b82f6]" />
      <StatCard icon="ri-book-open-line" label="Bugünkü Aktif Dersler" value={stats.todayActiveLessons} subLabel="Planlandı / Tamamlandı" iconBg="bg-indigo-50" iconColor="text-indigo-600" />
      <StatCard icon="ri-bar-chart-2-line" label="Bu Hafta Yapılan Ders" value={stats.weeklyLessons} subLabel="7 günlük toplam" iconBg="bg-purple-50" iconColor="text-[#7c6af5]" />
      <StatCard icon="ri-money-dollar-circle-line" label="Bu Ay Toplam Ciro" value={`${stats.monthlyRevenue.toLocaleString('tr-TR')} ₺`} subLabel="Onaylı tahsilatlar" iconBg="bg-emerald-50" iconColor="text-emerald-600" highlight />
      <StatCard icon="ri-coins-line" label="Öğretmen Hak Edişleri" value={`${stats.monthlyTeacherCommission.toLocaleString('tr-TR')} ₺`} subLabel="Bu ay toplam" iconBg="bg-amber-50" iconColor="text-amber-600" />
      <StatCard icon="ri-time-line" label="Bekleyen Dekontlar" value={stats.pendingReceipts} subLabel="Onay bekleniyor" iconBg="bg-red-50" iconColor="text-red-500" trend={{ value: 'Onay bekliyor', up: false }} />
      <StatCard icon="ri-user-received-line" label="Dönüşüm (Bu Ay)" value={stats.trialConversions} subLabel="Deneme → Aktif Derse Döndü" iconBg="bg-teal-50" iconColor="text-teal-600" />
      <StatCard icon="ri-user-star-line" label="Aktif Öğretmen" value={stats.activeTeachers} subLabel="Sistemde kayıtlı" iconBg="bg-slate-50" iconColor="text-slate-600" />
    </div>
  );
}